/**
 * Shared chart utilities for rebate forecasting components
 * Clean rewrite to preserve all prediction points without data loss
 */

import { RebatePoint, UnifiedForecastResult } from '@erp/shared-types';
import { formatFullDateDisplay, formatMonthDisplay } from './dateUtils';
import { getRebateAmount } from './rebateAmountSelector';

/**
 * Individual chart data point - one per data type per day
 */
export interface ChartPoint {
  day: number; // daysFromStart for sorting
  date: Date; // actual date for this point
  dateStr: string; // formatted for x-axis (e.g., "Jan 30")
  fullDateStr: string; // formatted for tooltip (e.g., "January 30, 2025")
  type: string; // "estimates" | "linear-regression" | "polynomial" etc.
  value: number; // the rebate amount (not optional)
}

/**
 * Processed forecast data ready for chart rendering
 */
export interface ProcessedForecastData {
  points: ChartPoint[]; // ALL data points (estimates + all predictions)
  types: string[]; // ["estimates", "linear-regression", etc.]
  totals: {
    actualToDate: number; // sum of estimates so far
    projectedTotal: number; // final prediction value
    lastActualDay: number; // where estimates end
  };
  dateRange: {
    start: Date;
    end: Date;
  };
}

/**
 * Recharts-compatible data structure
 */
export interface RechartsDataPoint {
  day: number;
  dateStr: string;
  fullDateStr: string;
  [dataType: string]: number | string; // estimates, linear-regression, etc.
}

// --- Main entry point ---
export const transformForecastData = (
  forecast: UnifiedForecastResult,
  isCumulative = false,
  predictionSplitDay?: number | null,
): ProcessedForecastData => {
  // 1️⃣ Detect multi-year context
  const hasMultipleYears = detectMultipleYears(forecast);

  // 2️⃣ Generate chart points - backend handles all zero-filling, frontend just displays
  const points: ChartPoint[] = [];
  const types: string[] = [];

  // Simply map backend data to chart points - no filtering, no manipulation
  addEstimatePoints(points, types, forecast, hasMultipleYears, isCumulative);
  addPredictionPoints(
    points,
    types,
    forecast,
    hasMultipleYears,
    isCumulative,
    predictionSplitDay,
  );
  addReportedPoints(
    points,
    types,
    forecast,
    hasMultipleYears,
    isCumulative,
    predictionSplitDay,
  );

  // Only add Jan 1st baseline points to start the chart
  addBaselinePoints(points, types, forecast, hasMultipleYears);

  logChartDebugInfo(points, types);

  // 4️⃣ Compute summary totals and date range
  const totals = computeTotals(points, isCumulative);
  const dateRange = inferDateRange(points, forecast);

  return { points, types, totals, dateRange };
};

function detectMultipleYears(forecast: UnifiedForecastResult): boolean {
  const allDates: Date[] = [];

  // Helper to ensure date is a Date object
  const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
  };

  forecast.estimates?.forEach((e) => allDates.push(toDate(e.date)));
  forecast.predictions &&
    Object.values(forecast.predictions).forEach((predPoints) =>
      predPoints.forEach((p) => allDates.push(toDate(p.date))),
    );
  forecast.reported?.forEach((r) => allDates.push(toDate(r.date)));

  const years = new Set(allDates.map((d) => d.getFullYear()));
  return years.size > 1;
}

function addEstimatePoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
  isCumulative: boolean,
) {
  // Bail if no estimates exist
  if (!forecast.estimates?.length) return;

  // Backend handles all zero-filling - just map the data directly
  const estimatePoints: ChartPoint[] = forecast.estimates.map((e) => ({
    day: e.daysFromStart,
    date: e.date,
    dateStr: formatMonthDisplay(e.date, hasMultipleYears),
    fullDateStr: formatFullDateDisplay(e.date),
    type: 'estimates' as const,
    value: getRebateAmount(e, isCumulative),
  }));

  // Add type and push all estimates exactly as backend sent them
  if (!types.includes('estimates')) types.push('estimates');
  points.push(...estimatePoints);
}

function addPredictionPoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
  isCumulative: boolean,
  predictionSplitDay?: number | null,
) {
  if (!forecast.predictions) return;

  // Helper to ensure date is a Date object
  const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
  };

  // Backend handles all zero-filling - just map the data directly
  Object.entries(forecast.predictions).forEach(
    ([method, predPoints]: [string, RebatePoint[]]) => {
      if (!predPoints?.length) return;

      // For linear-regression, split into past (magenta) and future (blue) if split day is provided
      if (
        method === 'linear-regression' &&
        predictionSplitDay !== null &&
        predictionSplitDay !== undefined
      ) {
        const pastPoints: ChartPoint[] = [];
        const futurePoints: ChartPoint[] = [];
        let connectionPoint: ChartPoint | null = null;

        predPoints.forEach((p) => {
          const isPast = p.daysFromStart <= predictionSplitDay;
          const chartPoint: ChartPoint = {
            day: p.daysFromStart,
            date: p.date,
            dateStr: formatMonthDisplay(toDate(p.date), hasMultipleYears),
            fullDateStr: formatFullDateDisplay(toDate(p.date)),
            type: isPast ? 'linear-regression-past' : method,
            value: getRebateAmount(p, isCumulative),
          };

          if (isPast) {
            pastPoints.push(chartPoint);
            // Track the point at the split day (this will be our connection point)
            if (p.daysFromStart === predictionSplitDay) {
              connectionPoint = chartPoint;
            }
          } else {
            futurePoints.push(chartPoint);
          }
        });

        // If we didn't find an exact match at split day, use the last past point
        if (!connectionPoint && pastPoints.length > 0) {
          connectionPoint = pastPoints[pastPoints.length - 1];
        }

        // Add connection point: duplicate the last past point for the future line
        // This ensures both lines share a point at the split, creating a visual connection
        if (connectionPoint && futurePoints.length > 0) {
          const futureConnectionPoint: ChartPoint = {
            ...connectionPoint,
            type: method, // Use original method name for future
          };
          futurePoints.unshift(futureConnectionPoint);
        }

        // Add types and push points
        if (pastPoints.length > 0) {
          if (!types.includes('linear-regression-past'))
            types.push('linear-regression-past');
          points.push(...pastPoints);
        }

        if (futurePoints.length > 0) {
          if (!types.includes(method)) types.push(method);
          points.push(...futurePoints);
        }
      } else {
        // For other prediction methods or when no split day, use original logic
        const methodKey = method;

        // Add all prediction points exactly as backend sent them
        predPoints.forEach((p) => {
          points.push({
            day: p.daysFromStart,
            date: p.date,
            dateStr: formatMonthDisplay(toDate(p.date), hasMultipleYears),
            fullDateStr: formatFullDateDisplay(toDate(p.date)),
            type: methodKey,
            value: getRebateAmount(p, isCumulative),
          });
        });

        // Add type if not already present
        if (!types.includes(methodKey)) types.push(methodKey);
      }
    },
  );
}

function addReportedPoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
  isCumulative: boolean,
  predictionSplitDay?: number | null,
) {
  // Bail if no reported values exist
  if (!forecast.reported?.length) return;

  // Helper to ensure date is a Date object
  const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
  };

  // DISABLED: Split functionality disabled - reported values are always black
  // To re-enable, see git history for the split logic based on predictionSplitDay
  const reportedPoints: ChartPoint[] = forecast.reported.map((r) => {
    const date = toDate(r.date);
    return {
      day: r.daysFromStart,
      date,
      dateStr: formatMonthDisplay(date, hasMultipleYears),
      fullDateStr: formatFullDateDisplay(date),
      type: 'reported' as const,
      value: getRebateAmount(r, isCumulative),
    };
  });
  if (!types.includes('reported')) types.push('reported');
  points.push(...reportedPoints);
}

function addBaselinePoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
) {
  if (!types.length) return;

  // Use the forecast period start date as the baseline (day 0)
  const periodStartDate =
    forecast.forecastPeriod?.startDate ||
    (points.length > 0
      ? new Date(Math.min(...points.map((p) => p.date.getTime())))
      : new Date());

  const baselineDay = 0; // Day 0 is the start of the forecast period

  // Determine if we're in "split mode" for predictions
  // Split mode = both past (magenta) and future (blue) prediction lines exist
  const isInPredictionSplitMode =
    types.includes('linear-regression-past') &&
    types.includes('linear-regression');

  // Determine if we're in "split mode" for reported
  // Split mode = both past (black) and future (blue) reported lines exist
  const isInReportedSplitMode =
    types.includes('reported') && types.includes('reported_future');

  // Only add baseline points at period start (day 0) - no other artificial points
  types.forEach((type) => {
    // Skip reported_future ONLY if we have black reported points to connect to
    // Edge case: In January when we only have future reported (no past reported yet),
    // we need a day 0 baseline for reported_future so the line starts from the origin
    if (type === 'reported_future' && isInReportedSplitMode) return;

    // In prediction split mode:
    // - linear-regression (blue/future) should NOT get a day 0 baseline - it starts at the split point
    // - linear-regression-past (magenta/past) SHOULD get a day 0 baseline
    // In non-split mode:
    // - linear-regression should get a day 0 baseline (it's the full line)
    if (isInPredictionSplitMode && type === 'linear-regression') return;

    // Check if this type already has a point at day 0
    const hasBaselinePoint = points.some(
      (p) => p.type === type && p.day === baselineDay,
    );
    if (!hasBaselinePoint) {
      points.push({
        day: baselineDay,
        date: periodStartDate,
        dateStr: formatMonthDisplay(periodStartDate, hasMultipleYears),
        fullDateStr: formatFullDateDisplay(periodStartDate),
        type,
        value: 0,
      });
    }
  });
}

function logChartDebugInfo(points: ChartPoint[], types: string[]) {
  if (process.env.NODE_ENV !== 'development') return;

  const safeConsoleTable = (
    label: string,
    rows: Array<Record<string, unknown>>,
  ) => {
    if (!rows.length) return;
    if (typeof console.table === 'function') {
      console.groupCollapsed?.(label);
      console.table(rows);
      console.groupEnd?.();
    } else {
      console.log(label, rows);
    }
  };

  // try {
  //   console.groupCollapsed?.('[ForecastChart] Data snapshot');
  //   console.log('[ForecastChart] Total points:', points.length);

  //   const pointPreview = points.slice(0, 50).map((point) => ({
  //     type: point.type,
  //     day: point.day,
  //     date: point.dateStr,
  //     value: Number(point.value.toFixed(2)),
  //   }));
  //   safeConsoleTable('[ForecastChart] First 50 points', pointPreview);

  //   const typeSummaries = types.map((type) => {
  //     const seriesPoints = points.filter((p) => p.type === type);
  //     if (!seriesPoints.length) {
  //       return { type, count: 0 };
  //     }

  //     const days = seriesPoints.map((p) => p.day);
  //     const values = seriesPoints.map((p) => p.value);
  //     return {
  //       type,
  //       count: seriesPoints.length,
  //       firstDay: Math.min(...days),
  //       lastDay: Math.max(...days),
  //       minValue: Number(Math.min(...values).toFixed(2)),
  //       maxValue: Number(Math.max(...values).toFixed(2)),
  //     };
  //   });
  //   safeConsoleTable('[ForecastChart] Type summaries', typeSummaries);
  // } catch (error) {
  //   console.warn('[ForecastChart] Failed to log debug info', error);
  // } finally {
  //   console.groupEnd?.();
  // }
}

function computeTotals(points: ChartPoint[], isCumulative: boolean) {
  const estimates = points.filter((p) => p.type === 'estimates');

  // For cumulative mode: use last point's value (already cumulative)
  // For incremental mode: sum all incremental values
  const actualToDate = estimates.length
    ? isCumulative
      ? estimates.reduce((latest, p) => (p.day > latest.day ? p : latest)).value
      : estimates.reduce((sum, p) => sum + p.value, 0)
    : 0;

  const lastActualDay = estimates.length
    ? Math.max(...estimates.map((p) => p.day))
    : 0;

  let projectedTotal = actualToDate;
  // Get predictions (exclude estimates and reported)
  const preds = points.filter(
    (p) => p.type !== 'estimates' && p.type !== 'reported',
  );
  if (preds.length) {
    // Group by method (no more past/future suffixes)
    const byMethod = new Map<string, ChartPoint[]>();
    preds.forEach((p) => {
      const method = p.type;
      if (!byMethod.has(method)) byMethod.set(method, []);
      byMethod.get(method)!.push(p);
    });

    const methods = Array.from(byMethod.keys());
    const methodToUse = methods.includes('linear-regression')
      ? 'linear-regression'
      : methods[0];
    const methodPoints = byMethod.get(methodToUse) || [];
    if (methodPoints.length) {
      // For cumulative mode: use last point's value (already cumulative)
      // For incremental mode: sum all incremental values
      if (isCumulative) {
        const lastPoint = methodPoints.reduce((latest, p) =>
          p.day > latest.day ? p : latest,
        );
        projectedTotal = lastPoint.value;
      } else {
        projectedTotal = methodPoints.reduce((sum, p) => sum + p.value, 0);
      }
    }
  }

  return { actualToDate, projectedTotal, lastActualDay };
}

function inferDateRange(points: ChartPoint[], forecast: UnifiedForecastResult) {
  const pointDates = points.map((p) => p.date);
  return {
    start:
      forecast.forecastPeriod?.startDate ||
      new Date(Math.min(...pointDates.map((d) => d.getTime()))),
    end:
      forecast.forecastPeriod?.endDate ||
      new Date(Math.max(...pointDates.map((d) => d.getTime()))),
  };
}

/**
 * Convert ProcessedForecastData to Recharts-compatible format
 */
export const convertToRechartsData = (
  processedData: ProcessedForecastData,
): RechartsDataPoint[] => {
  const dayMap = new Map<number, RechartsDataPoint>();

  processedData.points.forEach((point) => {
    if (!dayMap.has(point.day)) {
      dayMap.set(point.day, {
        day: point.day,
        dateStr: point.dateStr,
        fullDateStr: point.fullDateStr,
      });
    }
    const dataPoint = dayMap.get(point.day);
    if (dataPoint) {
      dataPoint[point.type] = point.value;
    }
  });

  const rechartsData = Array.from(dayMap.values()).sort(
    (a, b) => a.day - b.day,
  );

  return rechartsData;
};

/**
 * Get the most recent actual position for display
 * Returns a proper RebatePoint from the original forecast data
 */
export const getCurrentActualPosition = <T extends { daysFromStart: number }>(
  estimates: T[],
): T | null => {
  if (!estimates?.length) return null;

  // Sort by daysFromStart and get the last one
  const sortedEstimates = [...estimates].sort(
    (a, b) => a.daysFromStart - b.daysFromStart,
  );
  return sortedEstimates[sortedEstimates.length - 1];
};

/**
 * Standard chart formatting utilities
 */
export const chartFormatters = {
  /**
   * Format Y-axis ticks with currency notation and consistent spacing
   */
  formatYAxis: (value: number): string => {
    if (value === 0) return '$0';

    if (value >= 1000000) {
      // For millions, show one decimal place for cleaner spacing
      const millions = value / 1000000;
      return '$' + millions.toFixed(1) + 'M';
    } else if (value >= 1000) {
      // For thousands, show whole numbers for cleaner spacing
      const thousands = value / 1000;
      return '$' + Math.round(thousands) + 'K';
    } else {
      // For smaller values, show as whole dollars
      return '$' + Math.round(value);
    }
  },

  /**
   * Format X-axis ticks using chart data
   */
  formatXAxis: (value: number, chartData: RechartsDataPoint[]): string => {
    const dataPoint = chartData.find((d) => d.day === value);
    return dataPoint ? dataPoint.dateStr : value.toString();
  },
};

/**
 * Standard chart configuration objects
 */
export const chartConfig = {
  /**
   * Standard margin settings for forecast charts
   */
  chartMargin: { top: 20, right: 30, left: 20, bottom: 60 },

  /**
   * Standard height for forecast charts
   */
  chartHeight: 600,

  /**
   * Standard legend configuration
   */
  legendStyle: {
    paddingTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
  },

  /**
   * Y-axis domain calculation for better visualization
   */
  getYAxisDomain: (): [number, (dataMax: number) => number] => [
    0, // Always start Y-axis at 0
    (dataMax: number) => dataMax * 1.02,
  ],

  /**
   * Calculate 4 clean, evenly-spaced Y-axis ticks
   * Handles both positive-only and negative values
   */
  getYAxisTicks: (minValue: number, maxValue: number): number[] => {
    if (maxValue === 0 && minValue === 0) return [0];

    // If there are negative values, calculate ticks spanning the full range
    if (minValue < 0) {
      const range = maxValue - minValue;
      // Use one magnitude smaller for tighter bounds
      const magnitude = Math.pow(
        10,
        Math.floor(Math.log10(Math.abs(range))) - 1,
      );
      const normalizedRange = range / magnitude;
      const roundedRange = Math.ceil(normalizedRange) * magnitude;

      // Calculate step size
      const step = roundedRange / 4;

      // Start from rounded min value
      const roundedMin = Math.floor(minValue / magnitude) * magnitude;
      return [
        roundedMin,
        roundedMin + step,
        roundedMin + step * 2,
        roundedMin + step * 3,
        roundedMin + roundedRange,
      ];
    }

    // Positive values only - start at 0
    // Use one magnitude smaller for tighter bounds
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)) - 1);
    const normalized = maxValue / magnitude;
    const roundedMax = Math.ceil(normalized) * magnitude;

    // Create 4 evenly spaced ticks including 0
    const step = roundedMax / 4;
    return [0, step, step * 2, step * 3, roundedMax];
  },
};
